import { defineField, defineType } from 'sanity';

export default defineType({
  name: 'siteSettings',
  title: 'Site Settings',
  type: 'document',
  fields: [
    defineField({
      name: 'title',
      title: 'Site Title',
      type: 'string',
      initialValue: 'WyattGo',
      validation: Rule => Rule.required(),
    }),
    defineField({
      name: 'legalName',
      title: 'Legal Entity Name',
      type: 'string',
      description: 'Used in the footer, JSON-LD, and legal pages',
      initialValue: 'WyattGo Solutions LLC',
    }),
    defineField({
      name: 'tagline',
      title: 'Tagline',
      type: 'string',
      initialValue:
        'Software consulting for teams that need it working, not demoed',
    }),
    defineField({
      name: 'description',
      title: 'Site Description',
      type: 'text',
      description: 'Default meta description for the site',
      initialValue:
        'Software consulting: custom applications, workflow automation, systems integration, and AI features. Cincinnati-based, working with teams anywhere.',
    }),

    defineField({
      name: 'contactInfo',
      title: 'Contact Information',
      type: 'object',
      fields: [
        defineField({
          name: 'phone',
          title: 'Phone Number',
          type: 'string',
        }),
        defineField({
          name: 'email',
          title: 'Email Address',
          type: 'string',
          initialValue: 'hello@wyattgo.com',
          validation: Rule => Rule.required().email(),
        }),
        defineField({
          name: 'inquiriesEmail',
          title: 'New Business Email',
          type: 'string',
          description: 'Where the contact form delivers project inquiries',
          initialValue: 'hello@wyattgo.com',
          validation: Rule => Rule.required().email(),
        }),
        defineField({
          name: 'schedulingUrl',
          title: 'Scheduling Link',
          type: 'url',
          description: 'Optional Cal.com / Calendly link for an intro call',
        }),
        defineField({
          name: 'location',
          title: 'Location',
          type: 'object',
          description:
            'Shown for credibility. No street address needed for a remote practice.',
          fields: [
            defineField({
              name: 'city',
              title: 'City',
              type: 'string',
              initialValue: 'Cincinnati',
            }),
            defineField({
              name: 'state',
              title: 'State',
              type: 'string',
              initialValue: 'OH',
            }),
            defineField({
              name: 'country',
              title: 'Country',
              type: 'string',
              initialValue: 'USA',
            }),
            defineField({
              name: 'remoteNote',
              title: 'Remote Note',
              type: 'string',
              initialValue: 'Working remotely with teams across the US',
            }),
          ],
        }),
      ],
    }),

    defineField({
      name: 'availability',
      title: 'Availability',
      type: 'object',
      fields: [
        defineField({
          name: 'timezone',
          title: 'Primary Timezone',
          type: 'string',
          initialValue: 'America/New_York (ET)',
        }),
        defineField({
          name: 'responseTime',
          title: 'Response Time',
          type: 'string',
          description: 'Promise made on the contact form',
          initialValue: 'Replies within one business day',
        }),
        defineField({
          name: 'acceptingWork',
          title: 'Accepting New Work',
          type: 'boolean',
          initialValue: true,
        }),
        defineField({
          name: 'availabilityNote',
          title: 'Availability Note',
          type: 'string',
          description: 'e.g. "Booking new projects from March"',
        }),
      ],
    }),

    defineField({
      name: 'trustChips',
      title: 'Trust Chips',
      type: 'array',
      of: [{ type: 'string' }],
      description: 'Short credibility signals displayed on the homepage',
      initialValue: [
        'Fixed-fee options',
        'You own the code',
        'Direct access to the engineer',
        'Cincinnati based, remote friendly',
      ],
    }),

    defineField({
      name: 'homePageSettings',
      title: 'Homepage Settings',
      type: 'object',
      fields: [
        defineField({
          name: 'heroHeadline',
          title: 'Hero Headline',
          type: 'string',
          initialValue: "Your software works. It shouldn't look its age.",
        }),
        defineField({
          name: 'heroSubheadline',
          title: 'Hero Subheadline',
          type: 'text',
          initialValue:
            'Product design and frontend engineering for software that has outgrown its interface.',
        }),
        defineField({
          name: 'heroBackgroundImage',
          title: 'Hero Background Image',
          type: 'image',
          options: { hotspot: true },
        }),
        defineField({
          name: 'featuredServicesCount',
          title: 'Featured Services Count',
          type: 'number',
          initialValue: 3,
          validation: Rule => Rule.min(1).max(6),
        }),
        defineField({
          name: 'featuredCaseStudiesCount',
          title: 'Featured Case Studies Count',
          type: 'number',
          initialValue: 3,
          validation: Rule => Rule.min(0).max(6),
        }),
      ],
    }),

    defineField({
      name: 'seoSettings',
      title: 'SEO Settings',
      type: 'object',
      fields: [
        defineField({
          name: 'defaultTitle',
          title: 'Default Page Title',
          type: 'string',
          initialValue: 'WyattGo | Software Consulting',
        }),
        defineField({
          name: 'titleSuffix',
          title: 'Title Suffix',
          type: 'string',
          description: 'Appended to page titles',
          initialValue: ' | WyattGo',
        }),
        defineField({
          name: 'ogImage',
          title: 'Default Open Graph Image',
          type: 'image',
          description: 'Default image for social media sharing',
        }),
      ],
    }),

    defineField({
      name: 'capabilities',
      title: 'Capabilities',
      type: 'object',
      description:
        'Replaces the old service-area block. Drives the "what we work in" section.',
      fields: [
        defineField({
          name: 'primaryStack',
          title: 'Primary Stack',
          type: 'array',
          of: [{ type: 'string' }],
          options: { layout: 'tags' },
          initialValue: [
            'TypeScript',
            'React',
            'Next.js',
            'Node',
            'Python',
            'Postgres',
          ],
        }),
        defineField({
          name: 'platforms',
          title: 'Platforms & Infrastructure',
          type: 'array',
          of: [{ type: 'string' }],
          options: { layout: 'tags' },
          initialValue: ['AWS', 'Vercel', 'Cloudflare', 'Docker'],
        }),
        defineField({
          name: 'notOffered',
          title: 'What We Do Not Do',
          type: 'string',
          description:
            'Optional. Qualifying prospects out early saves everyone time.',
        }),
      ],
    }),

    defineField({
      name: 'socialMedia',
      title: 'Social & Profiles',
      type: 'object',
      fields: [
        defineField({ name: 'github', title: 'GitHub URL', type: 'url' }),
        defineField({ name: 'linkedin', title: 'LinkedIn URL', type: 'url' }),
        defineField({ name: 'x', title: 'X / Twitter URL', type: 'url' }),
        defineField({
          name: 'googleBusinessProfile',
          title: 'Google Business Profile URL',
          type: 'url',
        }),
      ],
    }),

    defineField({
      name: 'legalSettings',
      title: 'Legal Settings',
      type: 'object',
      fields: [
        defineField({
          name: 'entityType',
          title: 'Entity Type',
          type: 'string',
          initialValue: 'Ohio LLC',
        }),
        defineField({
          name: 'ipTerms',
          title: 'IP & Ownership Terms',
          type: 'text',
          description: 'Summary shown on service pages',
          initialValue:
            'You own all work product outright on final payment, including source code and infrastructure configuration.',
        }),
        defineField({
          name: 'insuranceInfo',
          title: 'Insurance Information',
          type: 'object',
          fields: [
            defineField({
              name: 'carrier',
              title: 'Insurance Carrier',
              type: 'string',
            }),
            defineField({
              name: 'coverage',
              title: 'Coverage Summary',
              type: 'string',
              description: 'e.g. professional liability / E&O limits',
            }),
          ],
        }),
      ],
    }),

    defineField({
      name: 'analyticsSettings',
      title: 'Analytics Settings',
      type: 'object',
      fields: [
        defineField({
          name: 'googleAnalyticsId',
          title: 'Google Analytics ID',
          type: 'string',
        }),
        defineField({
          name: 'googleTagManagerId',
          title: 'Google Tag Manager ID',
          type: 'string',
        }),
      ],
    }),
  ],
  preview: {
    prepare() {
      return {
        title: 'Site Settings',
        subtitle: 'Global site configuration',
      };
    },
  },
});
